import { useMemo } from "react";
import { TissueInformationFormProps } from "../../..";
import { FormCheck, Row } from "react-bootstrap";
import {
    displayDate,
    displayDateAndTime,
    displayTime,
    displayTimeZonelessDate,
    getValueOrBlank,
} from "../../../../helper-functions";
import { useReferralFormData, useTIFFormData } from "../../../../hooks";
import { TissueTypes } from "../../../../models";
import { AutoCol } from "../../../../component-library";

export function TIFDonorInfoLong({ tissueId }: Readonly<TissueInformationFormProps>) {
    const {
        tissue,
        _case,
        donor,
        recoveryData,
        dToPDisplayValue,
        latestProcedure,
        latestTissueProcedure,
        latestProcedurePerformedBy,
        originalStorageSolution,
        originalLotNumber,
        originalExpDate,
        laboratoryExcisionPerformedOn,
        laboratoryExcisionPerformedBy,
        procuredByUserName,
    } = useTIFFormData({ tissueId });

    const { donorAge, donorGender, donorRace, donorTimeOfDeathDateAndTime } = useReferralFormData(_case?.referral);

    const procedureName = useMemo(() => {
        switch (tissue?.tissueType?.name) {
            case TissueTypes.Dmek:
                return "DMEK";
            case TissueTypes.Precut:
            case TissueTypes.Precut1:
            case TissueTypes.Precut2:
                return "DSAEK";
            case TissueTypes.Cairs:
            case TissueTypes.Cairs1:
            case TissueTypes.CairsCtr:
            case TissueTypes.CairsSeg1:
            case TissueTypes.CairsSeg2:
                return "CAIRS";
            case TissueTypes.Lenticule:
                return "Lenticule";
        }
        return "";
    }, [tissue?.tissueType?.name]);

    return (
        <>
            <Row>
                <AutoCol>
                    Donor #: <b>{getValueOrBlank(donor?.number)}</b>
                </AutoCol>
                <AutoCol className="fw-bold">
                    Cause of Death: {getValueOrBlank(recoveryData?.PrimaryCauseOfDeath)}
                </AutoCol>
                <AutoCol>Secondary Cause of Death: {getValueOrBlank(recoveryData?.SecondaryCauseOfDeath)}</AutoCol>
            </Row>
            <Row>
                <AutoCol className="fw-bold">Age: {donorAge}</AutoCol>
                <AutoCol>Race: {donorRace}</AutoCol>
                <AutoCol>Sex: {donorGender}</AutoCol>
            </Row>
            <Row>
                <AutoCol className="fw-bold">Date/Time of Cardiac Death: {donorTimeOfDeathDateAndTime}</AutoCol>
                <AutoCol className="pe-3">
                    D/T Ocular Cooling began: {getValueOrBlank(displayDate(recoveryData?.OcularCoolingDate))}{" "}
                    {getValueOrBlank(displayTime(recoveryData?.OcularCoolingTime))}
                </AutoCol>
                {!!recoveryData?.ocularCoolingNA && (
                    <AutoCol>
                        <FormCheck disabled checked={recoveryData?.ocularCoolingNA} label="N/A" />
                    </AutoCol>
                )}
            </Row>
            <Row>
                <AutoCol className="fw-bold">
                    D/T of Preservation: {getValueOrBlank(displayDate(recoveryData?.ProcedureDate))}{" "}
                    {getValueOrBlank(displayTime(recoveryData?.ProcedureTime))}
                </AutoCol>
                <AutoCol>
                    Type:{" "}
                    {getValueOrBlank(
                        tissue?.side === "OD" ? recoveryData?.procedureTypeOD : recoveryData?.procedureTypeOS
                    )}
                </AutoCol>
                <AutoCol>By: {getValueOrBlank(procuredByUserName)}</AutoCol>
            </Row>
            <Row>
                <AutoCol className="fw-bold">Death to Preservation: {dToPDisplayValue} </AutoCol>
                <AutoCol>Preservation Solution: {originalStorageSolution}</AutoCol>
                <AutoCol>Lot No.: {originalLotNumber}</AutoCol>
                <AutoCol>Exp. Date: {originalExpDate}</AutoCol>
            </Row>
            <Row>
                <AutoCol>D/T Laboratory Excision: {laboratoryExcisionPerformedOn}</AutoCol>
                <AutoCol>By: {laboratoryExcisionPerformedBy?.name}</AutoCol>
                <AutoCol>
                    D/T {procedureName} Processing: {displayDateAndTime(latestProcedure?.performedOn)}
                </AutoCol>
                <AutoCol>By: {latestProcedurePerformedBy?.name}</AutoCol>
            </Row>
            <Row>
                <AutoCol>Processing Solution: {latestTissueProcedure?.storageSolutionName}</AutoCol>
                <AutoCol>Lot No. {latestTissueProcedure?.storageSolutionLotNumber}</AutoCol>
                <AutoCol>Exp Date: {displayTimeZonelessDate(latestTissueProcedure?.storageSolutionExpDate)}</AutoCol>
            </Row>
        </>
    );
}
